<template>
    <div class="workflow-state">
        <v-tooltip v-for="task in state" :key="task.id" bottom>
            <template v-slot:activator="{ on, attrs }">
                <div
                    :class="[task.calc_status === 'overdue' ? 'task-overdue' : '', isVisible(task) ? 'task-visible' : '']"
                    v-on="on"
                    @click="openClick(task)"
                >
                    <v-icon v-if="task.status === 'finished'" small>mdi-check</v-icon>
                    <v-icon v-else-if="hasActiveDependencies(task)" small>mdi-arrange-bring-to-front</v-icon>
                    <svg
                        v-else-if="isBlocking(task)" style="margin-top: 4.5px;" width="14" height="14" viewBox="0 0 12 12"
                        fill="none" xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M6 0C2.688 0 0 2.688 0 6C0 9.312 2.688 12 6 12C9.312 12 12 9.312 12 6C12 2.688 9.312 0 6 0ZM6 10.8C3.354 10.8 1.2 8.646 1.2 6C1.2 3.354 3.354 1.2 6 1.2C8.646 1.2 10.8 3.354 10.8 6C10.8 8.646 8.646 10.8 6 10.8Z" />
                        <path d="M7.79997 4.2L7.19998 3H3.59998V9.6H4.49998V6.6H5.99998L6.59998 7.8H9.59997V4.2H7.79997ZM8.69998 6.9H7.15798L6.55798 5.7H4.49998V3.9H6.64197L7.24197 5.1H8.69998V6.9Z" />
                    </svg>
                    <v-icon v-else-if="task.status === 'new'" small>mdi-circle-outline</v-icon>
                    <v-icon v-else-if="task.status === 'in_progress'" small>mdi-clock-outline</v-icon>
                    <div v-if="isVisible(task)" :class="['process-step', getColor(task)]"></div>
                </div>
            </template>
            <div class="task-tooltip-details">
                <div class="t-title">{{ task.title }}</div>
                <div>{{ getAssigneesNames(task.assignees) }}</div>
                <div>
                    <span>{{ task.date | format_date }}</span>
                    <span v-if="task.has_due_date"> / {{ task.due_date | format_date }}
                    </span>
                </div>
                <div>
                    <svg
                        v-if="task.status !== 'finished' && isBlocking(task)" style="margin-top: 4.5px;" width="14" height="14" viewBox="0 0 12 12"
                        fill="none" xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M6 0C2.688 0 0 2.688 0 6C0 9.312 2.688 12 6 12C9.312 12 12 9.312 12 6C12 2.688 9.312 0 6 0ZM6 10.8C3.354 10.8 1.2 8.646 1.2 6C1.2 3.354 3.354 1.2 6 1.2C8.646 1.2 10.8 3.354 10.8 6C10.8 8.646 8.646 10.8 6 10.8Z" />
                        <path d="M7.79997 4.2L7.19998 3H3.59998V9.6H4.49998V6.6H5.99998L6.59998 7.8H9.59997V4.2H7.79997ZM8.69998 6.9H7.15798L6.55798 5.7H4.49998V3.9H6.64197L7.24197 5.1H8.69998V6.9Z" />
                    </svg>
                    <v-icon v-else small>{{ getIcon(task) }}</v-icon>
                    <span>{{ getFriendlyStatus(task) }}</span>
                </div>
                <div v-if="task.calc_status === 'overdue'" class="t-overdue">
                    {{ task.overdue === 1 ? $t("1 day overdue") : $t("{0} days overdue").format(task.overdue) }}
                </div>
                <div v-if="task.depends_on.length > 0" class="t-depends">
                    <div><v-icon small>mdi-arrange-bring-to-front</v-icon> <span>{{ $t("Depends on") }}:</span></div>
                    <div v-for="depends_on_id in task.depends_on" :key="depends_on_id">{{ getTaskTitle(depends_on_id) }}</div>
                </div>
                <div v-if="isVisible(task)" class="t-legend">
                    <div :class="['process-legend', getColor(task)]"></div>
                    <span>{{ getColorDisp(task) }}</span>
                </div>
            </div>
        </v-tooltip>
    </div>
</template>

<script>
import TaskModule, { task_status } from "../../store/modules/task";
import UserModule from "@/store/modules/user";
import { getTaskColor, getColorRanges, getTaskColorDisp } from "../../composables/use-task-color";
import AppModule from "../../store/modules/app";
import { computed } from "@vue/composition-api";
import { $t } from "@/i18n";

export default {
    name: "WorkflowStateRow",
    components: {},
    props: {
        project: {
            type: Object,
            required: true
        },
        state: {
            type: Array,
            required: true
        }
    },
    setup(props) {
        const { progress_steps, custom_fields } = AppModule();
        const userModule = UserModule();
        const { openTask } = TaskModule();

        const colorRanges = computed(() => {
            if (progress_steps.value[props.project.account_id] === undefined)
                return [];
            if (custom_fields.value[props.project.account_id] === undefined)
                return [];
            return getColorRanges(
                progress_steps.value[props.project.account_id], props.project, custom_fields.value[props.project.account_id]
            );
        });

        const hasActiveDependencies = item => {
            let hasActiveDep = false;
            for (let dep_id of item.depends_on) {
                let dep_task = props.state.find(x => x.id === dep_id);
                if (dep_task == null)
                    continue;
                if (dep_task.status !== 'finished')
                    hasActiveDep = true;
            }
            return hasActiveDep;
        }
        const isBlocking = item => {
            for (let task of props.state) {
                for (let dep_id of task.depends_on) {
                    if (dep_id === item.id)
                        return true;
                }
            }
            return false;
        }
        const isVisible = item => {
            return (item.status_bit & task_status.VISIBLE) === task_status.VISIBLE;
        }
        const getColor = item => {
            return getTaskColor(colorRanges.value, item);
        }
        const getColorDisp = item => {
            return getTaskColorDisp(colorRanges.value, item);
        }
        const getIcon = item => {
            if (item.status === "finished") return "mdi-check";
            //if (hasActiveDependencies(item)) return "mdi-arrange-bring-to-front";
            if (isBlocking(item)) return "---";
            if (item.status === 'new') return "mdi-circle-outline";
            if (item.status === 'in_progress') return "mdi-clock-outline";
            return "";
        }
        const getAssigneesNames = assignees => {
            return assignees.map(x => {
                let member = userModule.getMemberById(x);
                return member == null ? "" : member.name;
            }).filter(x => x !== "").join(", ");
        }
        const getFriendlyStatus = item => {
            if (item.status === "finished") return $t("Done");
            if (isBlocking(item)) {
                if (item.status === 'new') return $t("Todo - Blocking");
                if (item.status === 'in_progress') return $t("In progress - Blocking");
            }
            if (item.status === 'new') return $t("Todo");
            if (item.status === 'in_progress') return $t("In progress");
            return "";
        }
        const getTaskTitle = task_id => {
            let task = props.state.find(x => x.id === task_id);
            return task == null ? "" : task.title;
        }
        const openClick = item => {
            let task_id = item.id != null && item.id !== 0 ? item.id : item.virtual_id;
            openTask(task_id);
        }

        return {
            hasActiveDependencies,
            isBlocking,
            isVisible,
            getColor,
            getColorDisp,
            getIcon,
            getAssigneesNames,
            getFriendlyStatus,
            getTaskTitle,
            openClick
        };
    }
}
</script>